import { getQueryErrorMessage } from '@skynet/data-helpers';
import type {
  BadRequestError,
  ConflictError,
  ForbiddenError,
  NotFoundError,
  ServiceUnavailableError,
  TooManyRequestsError,
} from './api.generated';

/**
 * Machine-readable error code sent by assistant-smm in every error body
 * (SKY-4778), derived from the generated error schemas. Codes are the backend's
 * i18n keys and are namespaced (`exceptions.*` vs `social-post.exceptions.*`),
 * so they are kept whole: `exceptions.NOT_FOUND` and
 * `social-post.exceptions.NOT_FOUND` are different errors.
 */
export type SmmErrorCode =
  | BadRequestError['code']
  | ConflictError['code']
  | ForbiddenError['code']
  | NotFoundError['code']
  | ServiceUnavailableError['code']
  | TooManyRequestsError['code']
  | typeof SMM_RATE_LIMITED;

/** Text profile a variant error refers to (on `BAD_REQUEST_*VARIANT*`). */
export type SmmErrorProfile = NonNullable<BadRequestError['profile']>;

/**
 * Code reported for a 429 that carries no SMM error body (e.g. throttled by a
 * proxy in front of the service). A 429 from the service itself keeps its
 * own `exceptions.TOO_MANY_REQUESTS`; use {@link isSmmRateLimited} to match both.
 */
export const SMM_RATE_LIMITED = 'RATE_LIMITED';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object';

/** Normalised SMM API error, see {@link getSmmError}. */
export interface SmmError {
  /** HTTP status, or the RTK Query string status (`FETCH_ERROR`, `PARSING_ERROR`, ...). */
  status: number | string;
  /**
   * The HTTP status behind a `PARSING_ERROR`: the answer arrived with that
   * status but a body RTK Query could not read as JSON (a gateway's HTML error
   * page in front of the service, say). Only `status` says what went wrong at
   * the transport level, so anything branching on the HTTP status has to read
   * this one too.
   */
  originalStatus?: number;
  /**
   * Machine-readable code, when the response carried one. Not validated
   * against {@link SmmErrorCode} at runtime, so a code added by a newer
   * backend passes through as a plain string.
   */
  code?: SmmErrorCode | (string & {});
  /** Human-readable message (localized by the backend when it sent one). */
  message: string;
  /** Text profile a variant error refers to. */
  profile?: SmmErrorProfile;
  /** Field-level failures, on `exceptions.BAD_REQUEST_VALIDATION`. */
  errors?: SmmFieldError[];
}

/** One failed field of a `BAD_REQUEST_VALIDATION` error. */
export interface SmmFieldError {
  /** JSON path of the field (e.g. `/variants/0/content`). */
  path: string;
  message: string;
}

const isFieldError = (value: unknown): value is SmmFieldError =>
  isRecord(value) && typeof value.path === 'string' && typeof value.message === 'string';

/**
 * Normalises an error thrown by an SMM endpoint (`.unwrap()`, `error` from a
 * hook, or the upload helper) into {@link SmmError}.
 *
 * Handles HTTP errors with an SMM body, HTTP errors without one, RTK Query
 * transport errors (`FETCH_ERROR`, `PARSING_ERROR`, `TIMEOUT_ERROR`) and plain
 * `Error`s. The message follows `getQueryErrorMessage` (`data.message`,
 * `data.error`, transport error, text body, ...), like every other API package.
 *
 * @param err - Unknown error value.
 * @returns The normalised error; never throws.
 */
export const getSmmError = (err: unknown): SmmError => {
  if (isRecord(err) && 'status' in err) {
    const status = typeof err.status === 'number' ? err.status : String(err.status);
    const data = isRecord(err.data) ? err.data : undefined;
    const code = typeof data?.code === 'string' ? data.code : undefined;
    const profile =
      data?.profile === 'twitter' || data?.profile === 'default' ? data.profile : undefined;
    const errors = Array.isArray(data?.errors)
      ? data.errors.filter(isFieldError).map(({ path, message }) => ({ path, message }))
      : undefined;

    return {
      status,
      ...(typeof err.originalStatus === 'number' && { originalStatus: err.originalStatus }),
      code: code ?? (status === 429 ? SMM_RATE_LIMITED : undefined),
      message: getQueryErrorMessage(err),
      ...(profile && { profile }),
      ...(errors?.length && { errors }),
    };
  }

  return {
    status: 'UNKNOWN_ERROR',
    message: getQueryErrorMessage(err, String(err)),
  };
};

/**
 * Whether `err` is an SMM API error carrying the given code.
 *
 * @example
 * ```ts
 * if (isSmmError(err, 'social-post.exceptions.CONFLICT_CLAIMED')) refetch();
 * ```
 */
export const isSmmError = (err: unknown, code: SmmErrorCode): boolean =>
  getSmmError(err).code === code;

/** Whether `err` is a rate-limit rejection, from the service or from a proxy. */
export const isSmmRateLimited = (err: unknown): boolean => {
  const { code } = getSmmError(err);

  return code === 'exceptions.TOO_MANY_REQUESTS' || code === SMM_RATE_LIMITED;
};

/**
 * 403 codes of the `SocialPost` policy refusing the post **in its current
 * status**. assistant-smm (#396) loads every post it writes through the
 * policy's query plan and answers `exceptions.FORBIDDEN` when the plan's
 * status filter excludes it; cancel answers its own `FORBIDDEN_CANCEL`.
 *
 * Not to be confused with `FORBIDDEN_RESOURCE` / `FORBIDDEN_RESOURCE_ID`: the
 * Cerbos interceptor's refusal for the role (the plan is always denied), for
 * a collection or for one post id. Those name the scope, not the status.
 *
 * `exceptions.FORBIDDEN` is also the backend's generic code for a 403 thrown
 * without one. On these routes nothing else answers 403 today (the JWT guard
 * answers 401, the rate limiter 429), so it reads as a status refusal. Re-check
 * this if another layer (a guard, a gateway) starts answering 403s: it would be
 * taken for a moved-on post, and the composer would close and drop the input.
 */
const STATUS_REFUSED_CODES: ReadonlySet<string> = new Set<SmmErrorCode>([
  'exceptions.FORBIDDEN',
  'social-post.exceptions.FORBIDDEN_CANCEL',
]);

/**
 * Whether `err` is the `SocialPost` policy refusing the post in its current
 * status. Since assistant-smm checks statuses through Cerbos alone (SKY-4927),
 * this is how a post that moved on under the user (another tab, or the
 * publisher claimed it) is refused: read the post again rather than retry.
 *
 * Matched by code, so a 403 without one is never this: storage rejecting an
 * upload (`SocialPostMediaUploadError` with `S3_UPLOAD`, e.g. an expired
 * presigned policy) or a gateway's own error page.
 */
export const isSmmStatusRefused = (err: unknown): boolean => {
  const { status, code } = getSmmError(err);

  return status === 403 && code !== undefined && STATUS_REFUSED_CODES.has(code);
};
